import type { AnyFilter } from "../filter"
import { FilterSchema } from "../schemas"

export interface FilterManagerConfig<TData> {
	schema: FilterSchema<TData>
	filters: AnyFilter[]
}

export class FilterManager<TData> {
	private schema: FilterSchema<TData>
	private filters: AnyFilter[] = []
	private onFilterChange?: (filters: AnyFilter[]) => void

	private constructor(config: FilterManagerConfig<TData>) {
		const { schema, filters } = config

		this.schema = schema
		this.initFilters(filters)
	}

	static create<TData>(config: FilterManagerConfig<TData>) {
		return new FilterManager(config)
	}

	private initFilters = (filters: AnyFilter[]) => {
		for (const filter of filters) {
			filter.subscribe(this.subscribeToFilter)
		}

		this.filters = filters
	}

	private subscribeToFilter = (filter: AnyFilter) => {
		const filterIndex = this.filters.findIndex((f) => f.id === filter.id)
		if (filterIndex === -1) {
			return
		}
		this.filters[filterIndex] = filter
		this.filters = [...this.filters]
		this.onFilterChange?.(this.filters)
	}

	getFilters = () => {
		return this.filters
	}

	// coming from the outside
	setFilters = (filters: AnyFilter[]) => {
		this.initFilters(filters)
	}

	setOnFilterChange = (onFilterChange?: (filters: AnyFilter[]) => void) => {
		this.onFilterChange = onFilterChange
	}

	setSchema = (schema: FilterSchema<TData>) => {
		this.schema = schema
	}

	applyFilters = (filters: AnyFilter[], data: TData[]) => {
		const applyAndAccessorFns = filters.map((filter) => [
			filter.getApplyFn(),
			this.schema.getAccessorFn(filter.filterDef.id),
		])

		return data.filter((item) => {
			return applyAndAccessorFns.every(([applyFn, accessorFn]) => {
				return applyFn(accessorFn(item))
			})
		})
	}

	addFilter = (filter: AnyFilter) => {
		filter.subscribe(this.subscribeToFilter)
		this.initFilters([...this.filters, filter])
		this.onFilterChange?.(this.filters)
	}

	removeFilter = (filterId: string) => {
		this.filters = this.filters.filter((filter) => filter.id !== filterId)
		this.initFilters(this.filters)
		this.onFilterChange?.(this.filters)
	}

	removeAll = () => {
		this.filters = []
		this.onFilterChange?.(this.filters)
	}
}
